import { ACCESS_STORAGE_KEYS, getStoredValue } from "@lib/access/access";
import type { ResolveScopeContext, ScopeContextResolution } from "@sdk/spellbook";

export type TeamRequestContext =
    | { kind: "automatic" }
    | { kind: "selected-account" }
    | { kind: "selected-network" }
    | {
          kind: "organisation";
          organisationId: string;
          via: "account" | "network" | "accountOrNetwork";
      };

declare module "@sdk/spellbook" {
    interface SpellbookRegister {
        requestContext: TeamRequestContext;
    }
}

let insiderPrincipal: string | null = null;

export function clearScopeInsiderCapability(): void {
    insiderPrincipal = null;
}

export function setScopeInsiderCapability(principalId: string | null, isInsider: boolean): void {
    if (!principalId || principalId !== getStoredValue(ACCESS_STORAGE_KEYS.userId)) return;
    insiderPrincipal = isInsider ? principalId : null;
}

export const resolveTeamScopeContext: ResolveScopeContext = ({ operation, requestContext }) => {
    const userId = getStoredValue(ACCESS_STORAGE_KEYS.userId);
    const token = getStoredValue(ACCESS_STORAGE_KEYS.loginToken);
    const authenticated = Boolean(userId && token);
    const accountId = authenticated
        ? getStoredValue(ACCESS_STORAGE_KEYS.activeOrganisationId)
        : null;
    const networkId = authenticated ? getStoredValue(ACCESS_STORAGE_KEYS.networkId) : null;
    const logasId = authenticated ? getStoredValue(ACCESS_STORAGE_KEYS.logasId) : null;
    const required = operation.requiredScopes ?? [];
    const shouldSendAuthHeaders = authenticated && required.length > 0;
    const context: TeamRequestContext = requestContext ?? { kind: "automatic" };
    const availableScopes: string[] = authenticated ? ["authenticated"] : [];

    if (authenticated && insiderPrincipal === userId) availableScopes.push("logas");

    let target: string | null = null;
    let scopeKind: "none" | "account" | "network" = "none";
    let reason: string | undefined;

    // An explicit request context takes precedence over generated required scopes.
    switch (context.kind) {
        case "selected-account":
            scopeKind = "account";
            target = accountId;
            if (!target) reason = "No account is selected";
            break;

        case "selected-network":
            scopeKind = "network";
            target = networkId;
            if (!target) reason = "No network is available";
            break;

        case "organisation":
            target = context.organisationId.trim() || null;

            if (context.via === "accountOrNetwork") {
                scopeKind = !accountId && networkId ? "network" : "account";
            } else {
                scopeKind = context.via;
            }

            if (scopeKind === "network" && !networkId) {
                reason = "No network is available";
            } else if (!target) {
                reason = "An organisation target is required";
            }
            break;

        case "automatic":
            if (required.includes("account")) {
                scopeKind = "account";
                target = accountId;
            } else if (required.includes("network")) {
                scopeKind = "network";
                target = networkId;
            } else if (
                required.includes("accountOrNetwork") ||
                required.includes("authenticated")
            ) {
                target = accountId ?? networkId;
                scopeKind = accountId ? "account" : networkId ? "network" : "none";
            }
            break;
    }

    if (authenticated && target && !reason) {
        availableScopes.push(scopeKind, "accountOrNetwork");
        // A network session can supply a child account target to an account-scoped operation.
        if (context.kind === "organisation") availableScopes.push("account");
    }

    const result = {
        availableScopes: [...new Set(availableScopes)],
        cacheIdentity: [
            userId,
            logasId,
            scopeKind,
            target,
            scopeKind === "network" ? networkId : null,
        ],
        headers: {
            userId: shouldSendAuthHeaders ? userId : null,
            token: shouldSendAuthHeaders ? token : null,
            logasId: shouldSendAuthHeaders ? logasId : null,
            organisationId: shouldSendAuthHeaders ? target : null,
        },
    } satisfies Omit<ScopeContextResolution, "status" | "reason">;

    if (reason) return { ...result, status: "blocked", reason };
    return { ...result, status: "ready" };
};
